"use client";
import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { getCreditsSummary, getCreditsLogs } from "@/lib/credits-service"; // adjust path!
import { getCreditStatus, type CreditStatus } from "@/lib/services";
import { useCreditWebSocket } from "@/hooks/useCreditWebSocket";
import { getUserData } from "@/app/(main)/agency/(auth)/helper";
import { User } from '@prisma/client'
interface FeatureUsage {
  [key: string]: number;
}
export interface CreditsSummary {
  total_available: number
  total_used: number
  remaining: number
  plan_name?: string
  next_charge_date?: string
  feature_usage: FeatureUsage
  is_trial?: boolean
}
interface CreditsContextType {
  creditsSummary: CreditsSummary | null;
  creditStatus: CreditStatus | null;
  loading: boolean;
  error: string | null;
  fetchCreditsSummary: () => Promise<void>;
  clearCreditsData: () => void;
}
const CreditsContext = createContext<CreditsContextType | undefined>(undefined);
export const CreditsProvider = ({ children }: { children: ReactNode }) => {
  const [creditsSummary, setCreditsSummary] = useState<CreditsSummary | null>(null);
  const [creditStatus, setCreditStatus] = useState<CreditStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { creditStatus: wsCreditStatus, error: wsError, isConnected } = useCreditWebSocket();
  const [user, setUser] = useState<User | null>(null)
  useEffect(() => {
    const userData = getUserData();
    if (userData) {
      setUser(userData);
    }
  }, []);

  const fetchCreditsSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCreditsSummary();
      setCreditsSummary(data);
    } catch (err) {
      console.error("Error fetching credits summary:", err);
      setError("Failed to fetch credits summary.");
    } finally {
      setLoading(false);
    }
  }, []);

  const clearCreditsData = useCallback(() => {
    setCreditsSummary(null);
    setCreditStatus(null);
    setLoading(false);
    setError(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const handleClearCreditsData = () => {
      clearCreditsData();
    };

    window.addEventListener('clearCreditsData', handleClearCreditsData);
    return () => {
      window.removeEventListener('clearCreditsData', handleClearCreditsData);
    };
  }, [clearCreditsData]);

  useEffect(() => {
    const fetchCreditStatus = async () => {
      try {
        if (user) {
          const status = await getCreditStatus();
          setCreditStatus(status);
        }
      } catch (error) {
        console.error("Error fetching credit status:", error);
        setError("Failed to fetch credit status");
      }
    };
    fetchCreditStatus();
  }, []);

  useEffect(() => {
    if (wsCreditStatus) {
      setCreditStatus(wsCreditStatus);
    }
  }, [wsCreditStatus]);

  useEffect(() => {
    if (wsError) {
      setError(wsError);
      console.error("WebSocket error:", wsError);
    }
  }, [wsError]);

  return (
    <CreditsContext.Provider
      value={{
        creditsSummary,
        creditStatus,
        loading,
        error,
        fetchCreditsSummary,
        clearCreditsData
      }}
    >
      {children}
    </CreditsContext.Provider>
  );
};
// :five: Custom Hook
export const useCredits = () => {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error("useCredits must be used within a CreditsProvider");
  }
  return context;
};
export default CreditsProvider;